import dayjs from 'dayjs'

import { isObject } from '@app/typeGuards'
import { isNullable } from '@app/utils'
import { objectGet } from '@app/utils/object'

import { columnSearchSchemaOptionType } from '@app/hooks/useTableSearch/сonstants'

import type { ColumnTableSearchSchema, TableSearch } from '@app/hooks/useTableSearch/types'

const getDataSourceCellValue = (rawDataSourceCellValue: unknown, dataPath?: string[]): unknown => {
  if (isObject(rawDataSourceCellValue) && !isNullable(dataPath)) {
    return objectGet(rawDataSourceCellValue, dataPath)
  }

  return rawDataSourceCellValue
}

const isAllowedDataSourceRow = <ColumnsType, DataSourceType extends {}>({
  dataSourceRow,
  tableSearch,
  columnsSearchSchema
}: {
  dataSourceRow: DataSourceType
  tableSearch: TableSearch<ColumnsType>
  columnsSearchSchema: ColumnTableSearchSchema<ColumnsType>
}): boolean => {
  return Object.entries(dataSourceRow).every(([key, rawDataSourceCellValue]) => {
    const dataSourceKey = key as keyof ColumnsType
    const tableSearchValue = tableSearch[dataSourceKey]
    const columnsSearchOption = columnsSearchSchema[dataSourceKey]

    if (isNullable(tableSearchValue)) return true

    const { type, value } = tableSearchValue

    const dataSourceCellValue = getDataSourceCellValue(rawDataSourceCellValue, columnsSearchOption?.dataPath)

    switch (type) {
      case columnSearchSchemaOptionType.INPUT:
        return (dataSourceCellValue as string)?.toLowerCase()?.includes(value.toLowerCase())

      case columnSearchSchemaOptionType.NUMERIC_INPUT:
        return (dataSourceCellValue as number) === value

      case columnSearchSchemaOptionType.DATE_RANGE: {
        const from = dayjs(value.from, value.format)
        const to = dayjs(value.to, value.format)
        const date = dayjs(dataSourceCellValue as string, value.format)

        const isAfterOrEqual = date.isAfter(from) || date.isSame(from)
        const isBeforeOrEqual = date.isBefore(to) || date.isSame(to)

        return isAfterOrEqual && isBeforeOrEqual
      }

      // TODO: переработать, когда понадобится фильтрация по нескольким тегам
      // case columnSearchSchemaOptionType.MULTI_SELECT:
      //   return value?.selected?.includes(dataSourceCellValue as string)

      case columnSearchSchemaOptionType.SELECT:
        return (dataSourceCellValue as string)?.toLowerCase() === value.selected.toLowerCase()

      case columnSearchSchemaOptionType.NUMERIC_SELECT:
        return (dataSourceCellValue as number) === value.selected
    }

    return true
  })
}

export const getFilteredDataSource = <ColumnsType, DataSourceType extends {}>({
  dataSource,
  tableSearch,
  columnsSearchSchema
}: {
  dataSource?: DataSourceType[]
  tableSearch: TableSearch<ColumnsType>
  columnsSearchSchema: ColumnTableSearchSchema<ColumnsType>
}): DataSourceType[] | undefined => {
  return dataSource?.filter(dataSourceRow => {
    return isAllowedDataSourceRow({
      dataSourceRow,
      tableSearch,
      columnsSearchSchema
    })
  })
}
